import React from 'react';
import { PlusIcon, XIcon } from 'lucide-react';
import { BUTTON_QUIET, FIELD, SECTION_HINT, SECTION_TITLE, SUBSECTION_TITLE } from './adminStyles';
import { SectionSwitch } from './CallSheetSectionSwitches';
import { ShotReferenceUpload } from './ShotReferenceUpload';
import type { KitSectionName, ShootDayKitItem, ShootDayShotItem, ShotSectionName } from '../../utils/shootDaySource';

/**
 * The two grouped checklists on the Call Sheet Builder — Shots and Kit.
 *
 * Lifted out of CallSheetBuilder.tsx on 13 Sep 2026 when the segment
 * switches went in. They're the two sections that can be switched off, they
 * share the same "grouped rows with an add button per group" shape, and the
 * builder page was long enough already.
 *
 * Both take an `on` flag rather than being conditionally rendered by the
 * parent: a switched-off segment STAYS on the page, dimmed and marked, so
 * staff can keep filling it in for a future shoot. Hiding it would make the
 * switch feel like a delete, which is exactly the impression to avoid.
 */

const SHOT_SECTIONS: ShotSectionName[] = ['Essential', 'B-Roll', 'Optional'];
const KIT_SECTIONS: KitSectionName[] = ['Camera', 'Audio', 'Lighting', 'Media/Power'];

/** Shared chrome for a switchable section, so Shots, Kit and Files can't
    drift apart. The switch sits on the heading line itself — the question
    "do we need this on tomorrow's sheet?" occurs to you while you're looking
    at the section, so that's where the answer belongs. */
export function SwitchableSection({
  title,
  hint,
  on,
  onToggle,
  saving,
  children
}: {
  title: string;
  hint: string;
  on: boolean;
  onToggle: () => void;
  saving?: boolean;
  children: React.ReactNode;
}) {
  return <section className="mt-14 border-t border-ruleDark pt-8" data-id="element-545" data-mp="296j9ddbwrkr1">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3" data-id="element-546" data-mp="isilu485usts">
        <h2 className={`${SECTION_TITLE} ${on ? '' : 'text-paper/45'}`} data-id="element-547" data-mp="iuv5qmxhbd9h">{title}</h2>
        <SectionSwitch label={title} on={on} onToggle={onToggle} saving={saving} data-id="element-548" data-mp="et039f3tatu4" />
      </div>
      <p className={SECTION_HINT} data-id="element-549" data-mp="2dxi1pywrhvik">
        {on ? hint : 'Left off this call sheet — still yours to edit here, and one tap from coming back.'}
      </p>
      <div className={on ? '' : 'opacity-50'} data-id="element-550" data-mp="2nueewhxlv5tg">{children}</div>
    </section>;
}
interface ShotsSectionProps {
  on: boolean;
  onToggle: () => void;
  saving?: boolean;
  projectId: string;
  shots: ShootDayShotItem[];
  onAdd: (section: ShotSectionName) => void;
  onUpdate: (index: number, patch: Partial<ShootDayShotItem>) => void;
  onRemove: (index: number) => void;
}
export function ShotsSection({
  on,
  onToggle,
  saving,
  projectId,
  shots,
  onAdd,
  onUpdate,
  onRemove
}: ShotsSectionProps) {
  return <SwitchableSection title="Shots" hint="Portal's own addition, not on a standard call sheet. Reference images are optional — upload one straight from your device, no need to host it anywhere first." on={on} onToggle={onToggle} saving={saving} data-id="element-551" data-mp="3bjyzf59eqb4u">
      {SHOT_SECTIONS.map(section => {
      const rows = shots.map((item, index) => ({
        item,
        index
      })).filter(({
        item
      }) => item.section === section);
      return <div key={section} className="mt-8" data-id="element-552" data-mp="zw3wvfwpn72l">
            <span className={SUBSECTION_TITLE} data-id="element-553" data-mp="zwvn0b7jgclh">{section}</span>
            {rows.length === 0 && <p className="mt-3 font-display text-base text-paper/35" data-id="element-554" data-mp="uz3mwtwqpk67">Nothing added yet.</p>}
            <ul className="mt-4 space-y-4" data-id="element-555" data-mp="1sk7z1rg2p20j">
              {rows.map(({
            item,
            index
          }) => <li key={index} className="flex items-start gap-4 border border-paper/20 p-4" data-id="element-556" data-mp="1nmq10w3lg77b">
                  <div className="flex-1" data-id="element-557" data-mp="1itn208g1s5jq">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-558" data-mp="1dweju7uwg1ih">
                      <input value={item.name} onChange={event => onUpdate(index, {
                  name: event.target.value
                })} placeholder="Shot name" className={FIELD} data-id="element-559" data-mp="1duaj1ysarimf" />
                      <input value={item.cameraLabel} onChange={event => onUpdate(index, {
                  cameraLabel: event.target.value
                })} placeholder="Camera (optional)" className={FIELD} data-id="element-560" data-mp="1r1hiww2sb84p" />
                    </div>
                    <div className="mt-4" data-id="element-561" data-mp="1m5izlmwbcgb5">
                      <ShotReferenceUpload projectId={projectId} value={item.referenceImageUrl} onChange={url => onUpdate(index, {
                  referenceImageUrl: url
                })} data-id="element-562" data-mp="29ubo4yalvlvq" />
                    </div>
                  </div>
                  <button type="button" onClick={() => onRemove(index)} aria-label="Remove shot" className="shrink-0 text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-563" data-mp="251ikkdqvijl8">
                    <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-564" data-mp="m32qp7idcwbq" />
                  </button>
                </li>)}
            </ul>
            <button type="button" onClick={() => onAdd(section)} className={`${BUTTON_QUIET} mt-4 flex items-center gap-2`} data-id="element-565" data-mp="h2ouqnu1lcu2">
              <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-566" data-mp="qv4adehv9pbs" />
              Add {section} Shot
            </button>
          </div>;
    })}
    </SwitchableSection>;
}
interface KitSectionProps {
  on: boolean;
  onToggle: () => void;
  saving?: boolean;
  kit: ShootDayKitItem[];
  onAdd: (section: KitSectionName) => void;
  onUpdate: (index: number, patch: Partial<ShootDayKitItem>) => void;
  onRemove: (index: number) => void;
}
export function KitSection({
  on,
  onToggle,
  saving,
  kit,
  onAdd,
  onUpdate,
  onRemove
}: KitSectionProps) {
  return <SwitchableSection title="Kit" hint="Curated for this shoot, not an inventory." on={on} onToggle={onToggle} saving={saving} data-id="element-567" data-mp="1ima3haz66mrk">
      {KIT_SECTIONS.map(section => {
      const rows = kit.map((item, index) => ({
        item,
        index
      })).filter(({
        item
      }) => item.section === section);
      return <div key={section} className="mt-8" data-id="element-568" data-mp="p6xyvn9de9bw">
            <span className={SUBSECTION_TITLE} data-id="element-569" data-mp="u8dlvkv3ypwx">{section}</span>
            {rows.length === 0 && <p className="mt-3 font-display text-base text-paper/35" data-id="element-570" data-mp="3to1ulogc30za">Nothing added yet.</p>}
            <ul className="mt-4 space-y-4" data-id="element-571" data-mp="1khrimt0ha3b">
              {rows.map(({
            item,
            index
          }) => <li key={index} className="flex items-start gap-4 border border-paper/20 p-4" data-id="element-572" data-mp="6iau75v3vlyu">
                  <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-3" data-id="element-573" data-mp="b9j566c33fe4">
                    <input value={item.name} onChange={event => onUpdate(index, {
                name: event.target.value
              })} placeholder="Item name" className={FIELD} data-id="element-574" data-mp="39zj9pj2d29qe" />
                    <input value={item.quantity} onChange={event => onUpdate(index, {
                quantity: event.target.value
              })} placeholder="Qty (optional, e.g. x2)" className={FIELD} data-id="element-575" data-mp="37cr1bkm5kjvu" />
                    <input value={item.owner} onChange={event => onUpdate(index, {
                owner: event.target.value
              })} placeholder="Owner (optional, e.g. Hari)" className={FIELD} data-id="element-576" data-mp="2tej0v2pwr0fx" />
                  </div>
                  <button type="button" onClick={() => onRemove(index)} aria-label="Remove kit item" className="shrink-0 text-paper/30 transition-colors duration-150 ease-out hover:text-paper" data-id="element-577" data-mp="2eqftbow7vgp0">
                    <XIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-578" data-mp="20r6lbci5zsw4" />
                  </button>
                </li>)}
            </ul>
            <button type="button" onClick={() => onAdd(section)} className={`${BUTTON_QUIET} mt-4 flex items-center gap-2`} data-id="element-579" data-mp="3tlxmhmeotzmv">
              <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-580" data-mp="3jnzvir5g1oqk" />
              Add {section} Item
            </button>
          </div>;
    })}
    </SwitchableSection>;
}